/**
 * SmartPhotos — Photos intelligentes (Sprint V2 · P1).
 * ────────────────────────────────────────────────────
 * La brique la plus utilisée du produit. Le commercial NE classe rien.
 *
 *   • Un seul bouton lime « Ajouter des photos ».
 *   • Mobile : caméra / galerie.
 *   • Desktop : drag-drop + paste (Ctrl+V) + parcourir.
 *   • Chaque photo est classée par Gemini 3 Flash dans 1 des 9 catégories.
 *   • Grille par catégorie avec compteurs et chip.
 *   • Menu discret 3-points → Reclasser / Supprimer (agence uniquement).
 *
 * Contrainte produit stricte : jamais dix boutons, jamais deux endroits
 * pour la même action.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera, Image as ImageIcon, MoreHorizontal, Trash2, Loader2, UploadCloud,
  Check, AlertCircle,
} from "lucide-react";
import { Button, Card, Chip, MicroLabel } from "@/components/wyvio-ui";
import api from "@/lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORY_LABELS = {
  exterieur:          "Extérieur",
  interieur:          "Intérieur",
  cle:                "Clé",
  controle_technique: "Contrôle technique",
  cpi:                "CPI",
  facture:            "Facture",
  carnet_entretien:   "Carnet",
  diagnostic:         "Diagnostic",
  divers:             "Divers",
};
const CATEGORY_ORDER = [
  "exterieur", "interieur", "cle", "controle_technique",
  "cpi", "facture", "carnet_entretien", "diagnostic", "divers",
];

const openDoc = async (url) => {
  if (!url) return;
  try {
    if (url.startsWith("/api/files/")) {
      const res = await fetch(`${BASE}${url}`, { credentials: "include" });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } else {
      window.open(url, "_blank");
    }
  } catch (e) {
    console.error(e);
  }
};

const SmartPhotos = ({ dossierId, media = [], readOnly = false, onChange }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [uploads, setUploads] = useState([]); // [{tmpId, name, previewUrl, status:'analyse'|'done'|'error'}]
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [menuFor, setMenuFor] = useState(null); // media id
  const [thumbUrls, setThumbUrls] = useState({}); // id -> objectURL

  // Photos réelles seulement
  const photos = useMemo(
    () => (media || []).filter((m) => m.kind === "photo" && !m.archived_at),
    [media],
  );

  // Groupement par smart_category
  const grouped = useMemo(() => {
    const map = {};
    for (const p of photos) {
      const cat = p.smart_category || "divers";
      (map[cat] = map[cat] || []).push(p);
    }
    return CATEGORY_ORDER.filter((k) => map[k]).map((k) => [k, map[k]]);
  }, [photos]);

  // Charge les thumbnails via fetch authentifié
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const p of photos) {
        if (thumbUrls[p.id] || !p.url?.startsWith("/api/files/")) continue;
        try {
          const res = await fetch(`${BASE}${p.url}`, { credentials: "include" });
          if (!res.ok) continue;
          const blob = await res.blob();
          const obj = URL.createObjectURL(blob);
          if (cancelled) { URL.revokeObjectURL(obj); return; }
          setThumbUrls((prev) => ({ ...prev, [p.id]: obj }));
        } catch { /* silent */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length]);

  const doUpload = async (files) => {
    if (readOnly) return;
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (list.length === 0) return;
    setError("");
    const pendings = list.map((f) => ({
      tmpId: Math.random().toString(36).slice(2),
      name: f.name,
      previewUrl: URL.createObjectURL(f),
      status: "analyse",
    }));
    setUploads((u) => [...u, ...pendings]);
    // Upload séquentiel pour éviter de saturer Gemini
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      const tmp = pendings[i];
      try {
        await api.uploadPhoto(dossierId, f);
        setUploads((u) => u.map((x) => (x.tmpId === tmp.tmpId ? { ...x, status: "done" } : x)));
      } catch (e) {
        setUploads((u) => u.map((x) => (x.tmpId === tmp.tmpId ? { ...x, status: "error", err: e.message } : x)));
        setError(e.message || "Erreur upload");
      }
    }
    // Refresh dossier
    await onChange?.();
    // Nettoyage des pendings terminés au bout de 800ms
    setTimeout(() => setUploads((u) => u.filter((x) => x.status === "analyse")), 900);
  };

  // Drag & drop
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    doUpload(e.dataTransfer?.files || []);
  };

  // Paste (Ctrl+V)
  useEffect(() => {
    if (readOnly) return undefined;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
      if (files.length) doUpload(files);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, dossierId]);

  const reclassify = async (mediaId, cat) => {
    setMenuFor(null);
    try {
      await api.reclassifyPhoto(dossierId, mediaId, cat);
      await onChange?.();
    } catch (e) {
      setError(e.message || "Erreur reclassement");
    }
  };

  const removePhoto = async (mediaId) => {
    setMenuFor(null);
    if (!confirm("Supprimer cette photo ?")) return;
    try {
      await api.deletePhoto(dossierId, mediaId);
      setThumbUrls((prev) => {
        const clone = { ...prev };
        if (clone[mediaId]) URL.revokeObjectURL(clone[mediaId]);
        delete clone[mediaId];
        return clone;
      });
      await onChange?.();
    } catch (e) {
      setError(e.message || "Erreur suppression");
    }
  };

  return (
    <div data-testid="smart-photos" className="space-y-5">
      {/* Bouton principal + zone drag-drop unifiés */}
      {!readOnly && (
        <div
          data-testid="photos-hero"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={[
            "rounded-3xl border-2 border-dashed p-6 sm:p-8",
            "transition-all duration-200 ease-out",
            dragOver
              ? "border-[#C8FF2F] bg-[#F3FCE1] dark:bg-[#1F2A0A]"
              : "border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111]",
          ].join(" ")}
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="h-14 w-14 rounded-2xl bg-[#F3FCE1] dark:bg-[#1F2A0A] text-[#4a6b0f] dark:text-[#C8FF2F] flex items-center justify-center shrink-0">
              <UploadCloud size={20} strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[16px] sm:text-[18px] font-semibold text-[#0A0A0A] dark:text-white tracking-[-0.02em]">
                Ajouter des photos
              </p>
              <p className="mt-1 text-[12.5px] text-[#6B7280] dark:text-[#A3A3A3] leading-relaxed">
                <span className="sm:hidden">Prenez une photo ou choisissez dans la galerie — l&apos;IA les classe pour vous.</span>
                <span className="hidden sm:inline">Glissez-déposez, collez (Ctrl+V) ou parcourez — l&apos;IA classe automatiquement.</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              {/* Mobile : caméra */}
              <Button
                variant="primary"
                icon={Camera}
                data-testid="photos-camera-btn"
                onClick={() => cameraRef.current?.click()}
                className="sm:hidden"
              >
                Prendre une photo
              </Button>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => e.target.files && doUpload(e.target.files)}
              />
              <Button
                variant="dark"
                icon={ImageIcon}
                data-testid="photos-gallery-btn"
                onClick={() => galleryRef.current?.click()}
              >
                <span className="sm:hidden">Galerie</span>
                <span className="hidden sm:inline">Parcourir</span>
              </Button>
              <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                data-testid="photos-file-input"
                onChange={(e) => e.target.files && doUpload(e.target.files)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Uploads en cours (analyse IA visible) */}
      {uploads.length > 0 && (
        <div data-testid="photos-uploads" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {uploads.map((u) => (
            <div
              key={u.tmpId}
              className="relative rounded-2xl border border-[#EEF2F5] dark:border-[#262626] bg-white dark:bg-[#111111] overflow-hidden aspect-square"
            >
              <img src={u.previewUrl} alt={u.name} className="absolute inset-0 h-full w-full object-cover opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                {u.status === "analyse" ? (
                  <div className="flex flex-col items-center gap-1.5 text-center px-3">
                    <Loader2 size={20} className="animate-spin" />
                    <p className="text-[11px] font-semibold">Analyse IA…</p>
                  </div>
                ) : u.status === "error" ? (
                  <div className="flex flex-col items-center gap-1.5 text-center px-3">
                    <AlertCircle size={18} />
                    <p className="text-[10px]">{u.err || "Erreur"}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1.5 text-center px-3">
                    <Check size={18} strokeWidth={2.5} />
                    <p className="text-[11px] font-semibold">Ajouté</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <p data-testid="photos-error" className="text-[12.5px] text-[#991B1B]">
          {error}
        </p>
      )}

      {/* Grille groupée */}
      {photos.length === 0 && uploads.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#EEF2F5] dark:border-[#262626] p-8 text-center">
          <p className="text-[13.5px] text-[#6B7280] dark:text-[#A3A3A3]">
            Aucune photo pour ce dossier. Ajoutez-en une pour démarrer.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="photos-grid">
          {grouped.map(([cat, items]) => (
            <section key={cat} data-testid={`photos-cat-${cat}`}>
              <div className="flex items-center gap-2 mb-3">
                <MicroLabel>{CATEGORY_LABELS[cat] || cat}</MicroLabel>
                <span className="text-[11px] tabular-nums text-[#9CA3AF] dark:text-[#525252]">
                  · {items.length}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((p) => (
                  <PhotoTile
                    key={p.id}
                    photo={p}
                    thumbUrl={thumbUrls[p.id]}
                    readOnly={readOnly}
                    isMenuOpen={menuFor === p.id}
                    onMenuToggle={() => setMenuFor((m) => (m === p.id ? null : p.id))}
                    onOpen={() => openDoc(p.url)}
                    onReclassify={(newCat) => reclassify(p.id, newCat)}
                    onDelete={() => removePhoto(p.id)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const PhotoTile = ({
  photo, thumbUrl, readOnly, isMenuOpen, onMenuToggle, onOpen, onReclassify, onDelete,
}) => {
  const lowConfidence = photo.smart_confidence != null && photo.smart_confidence < 0.5;
  return (
    <div
      className="relative rounded-2xl border border-[#EEF2F5] dark:border-[#262626] bg-[#F3F4F6] dark:bg-[#0A0A0A] overflow-hidden group aspect-square"
      data-testid={`photo-tile-${photo.id}`}
    >
      <button
        onClick={onOpen}
        aria-label={`Ouvrir ${photo.name}`}
        className="absolute inset-0 w-full h-full text-left"
      >
        {thumbUrl ? (
          <img src={thumbUrl} alt={photo.name} className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-[#D1D5DB]">
            <Loader2 size={16} className="animate-spin" />
          </div>
        )}
      </button>

      {/* Badge confidence low (subtil) */}
      {lowConfidence && (
        <span
          data-testid={`photo-low-confidence-${photo.id}`}
          className="absolute top-2 left-2 rounded-full bg-white/90 dark:bg-[#111111]/90 text-[9.5px] font-semibold px-2 py-0.5 text-[#6B7280] pointer-events-none"
        >
          À vérifier
        </span>
      )}

      {/* Menu 3-points */}
      {!readOnly && (
        <>
          <button
            onClick={onMenuToggle}
            aria-label="Options"
            data-testid={`photo-menu-${photo.id}`}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/95 dark:bg-[#111111]/95 text-[#0A0A0A] dark:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          >
            <MoreHorizontal size={13} strokeWidth={2.25} />
          </button>
          {isMenuOpen && (
            <div
              data-testid={`photo-menu-open-${photo.id}`}
              className="absolute top-10 right-2 z-10 w-52 bg-white dark:bg-[#0A0A0A] rounded-2xl border border-[#EEF2F5] dark:border-[#262626] shadow-lg overflow-hidden text-[12px]"
            >
              <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6B7280] border-b border-[#EEF2F5] dark:border-[#262626]">
                Reclasser
              </div>
              <div className="max-h-56 overflow-y-auto">
                {CATEGORY_ORDER.map((c) => (
                  <button
                    key={c}
                    onClick={() => onReclassify(c)}
                    data-testid={`photo-reclass-${photo.id}-${c}`}
                    className={[
                      "w-full text-left px-3 py-2 hover:bg-[#F7F8FA] dark:hover:bg-[#111111] flex items-center justify-between",
                      photo.smart_category === c ? "bg-[#F3FCE1] dark:bg-[#1F2A0A] text-[#4a6b0f] dark:text-[#C8FF2F] font-semibold" : "text-[#0A0A0A] dark:text-white",
                    ].join(" ")}
                  >
                    {CATEGORY_LABELS[c]}
                    {photo.smart_category === c && <Check size={12} strokeWidth={2.5} />}
                  </button>
                ))}
              </div>
              <button
                onClick={onDelete}
                data-testid={`photo-delete-${photo.id}`}
                className="w-full text-left px-3 py-2 border-t border-[#EEF2F5] dark:border-[#262626] text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center gap-2"
              >
                <Trash2 size={12} strokeWidth={2} />
                Supprimer
              </button>
            </div>
          )}
        </>
      )}

      {/* Chip catégorie si menu fermé */}
      {!isMenuOpen && (
        <div className="absolute bottom-2 left-2 pointer-events-none">
          <Chip
            size="sm"
            tone={lowConfidence ? "neutral" : "accent"}
            className="!bg-white/95 dark:!bg-[#111111]/95 !text-[#0A0A0A] dark:!text-white shadow-sm"
          >
            {CATEGORY_LABELS[photo.smart_category] || "Divers"}
          </Chip>
        </div>
      )}
    </div>
  );
};

export default SmartPhotos;
